import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaymentsIcon from '@mui/icons-material/Payments';
import InboxIcon from '@mui/icons-material/Inbox';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import { numFont } from '../../utils/fonts';
import { formatDateOnly } from '../../utils';
import { formatPriceWithCurrency } from '../../utils/vat';
import DataTable from '../shared/DataTable';
import usePaginatedList from '../../hooks/usePaginatedList';
import { FlagIcons, KindChip, LinkChip, StatusChip } from './PurchaseChips';
import UploadDocumentsDialog from './UploadDocumentsDialog';
import ManualInvoiceDialog from './ManualInvoiceDialog';
import VendorAutocomplete from './VendorAutocomplete';
import { PL_API, TABS, invoiceUrl, purchaseErrorMessage, toNumber } from './purchaseUtils';

const TAB_ICONS = { received: InboxIcon };

function SummaryStrip({ summary }) {
  const { t } = useTranslation();
  if (!summary) return null;
  const items = [
    { key: 'received', value: summary.received, label: t('purchaseLedger.summary.received'), color: 'info.main' },
    {
      key: 'dueThisWeek',
      value: formatPriceWithCurrency(toNumber(summary.due_this_week?.total)),
      sub: t('purchaseLedger.summary.countPcs', { count: summary.due_this_week?.count ?? 0 }),
      label: t('purchaseLedger.summary.dueThisWeek'),
      color: 'text.primary',
    },
    {
      key: 'overdue',
      value: formatPriceWithCurrency(toNumber(summary.overdue?.total)),
      sub: t('purchaseLedger.summary.countPcs', { count: summary.overdue?.count ?? 0 }),
      label: t('purchaseLedger.summary.overdue'),
      color: toNumber(summary.overdue?.total) > 0 ? 'error.main' : 'text.primary',
    },
    {
      key: 'openTotal',
      value: formatPriceWithCurrency(toNumber(summary.open_total)),
      label: t('purchaseLedger.summary.openTotal'),
      color: 'text.primary',
    },
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }} data-testid="pl-summary">
      {items.map((item) => (
        <Paper key={item.key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>
            {item.label}
          </Typography>
          <Typography variant="h6" sx={{ ...numFont, fontWeight: 700, color: item.color, lineHeight: 1.2 }}>
            {item.value}
          </Typography>
          {item.sub && (
            <Typography variant="caption" color="text.secondary">
              {item.sub}
            </Typography>
          )}
        </Paper>
      ))}
    </Box>
  );
}

function MobileCards({ rows, onOpen, loading }) {
  const { t } = useTranslation();
  if (rows.length === 0 && loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (rows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        {t('purchaseLedger.list.empty')}
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {rows.map((row) => (
        <Card key={row.id} variant="outlined" onClick={() => onOpen(row)} sx={{ cursor: 'pointer' }} data-testid={`pl-card-${row.id}`}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                {row.vendor_name || t('purchaseLedger.vendor.unknown')}
              </Typography>
              <Typography variant="subtitle2" sx={{ ...numFont, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {formatPriceWithCurrency(toNumber(row.total_gross))}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {[row.invoice_number, row.number, row.description].filter(Boolean).join(' · ')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
              <StatusChip status={row.status} paymentState={row.payment_state} inBatch={row.in_open_batch} />
              <KindChip kind={row.kind} />
              <Typography variant="caption" color={row.is_overdue ? 'error.main' : 'text.secondary'} sx={numFont}>
                {row.due_date ? `${t('purchaseLedger.fields.dueShort')} ${formatDateOnly(row.due_date)}` : ''}
              </Typography>
              <FlagIcons flags={row.flags} />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function PurchaseInvoicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentOrganization } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = can(currentOrganization, 'purchase_invoices.manage');
  const canApprove = can(currentOrganization, 'purchase_invoices.approve');
  const canPay = can(currentOrganization, 'purchase_invoices.pay');

  const tabParam = searchParams.get('tab');
  const tab = TABS.includes(tabParam) ? tabParam : 'received';
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [vendor, setVendor] = useState(null);
  const [category, setCategory] = useState('');
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [bulkBusy, setBulkBusy] = useState(false);

  const vendorParam = searchParams.get('vendor');

  const filters = useMemo(() => {
    const f = { tab: tab === 'all' ? '' : tab };
    if (vendor?.id) f.vendor = vendor.id;
    else if (vendorParam) f.vendor = vendorParam;
    if (category) f.cost_category = category;
    if (dueFrom) f.due_from = dueFrom;
    if (dueTo) f.due_to = dueTo;
    if (search.trim()) f.q = search.trim();
    return f;
  }, [tab, vendor, vendorParam, category, dueFrom, dueTo, search]);

  const list = usePaginatedList(`${PL_API}/invoices/`, {
    initialFilters: filters,
    defaultSortField: 'created_at',
    defaultSortDirection: 'desc',
    defaultPageSize: 50,
  });

  useEffect(() => {
    const handle = setTimeout(() => list.setFilters(filters), 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${PL_API}/invoices/summary/`);
      setSummary(response.data);
    } catch (error) {
      // the strip just stays empty
    }
  }, []);

  useEffect(() => {
    loadSummary();
    axios
      .get(`${PL_API}/cost-categories/`)
      .then((r) => setCategories(Array.isArray(r.data) ? r.data : r.data?.results || []))
      .catch(() => setCategories([]));
  }, [loadSummary]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [tab]);

  const changeTab = (event, value) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const refresh = () => {
    list.refetch();
    loadSummary();
  };

  const selectedRows = list.data.filter((r) => selectedIds.has(r.id));
  const approvable = selectedRows.filter((r) => r.status === 'received');
  const payable = selectedRows.filter((r) => r.status === 'approved' && r.payment_state !== 'paid' && !r.in_open_batch && r.kind === 'invoice');

  const bulkApprove = async () => {
    setBulkBusy(true);
    let done = 0;
    try {
      for (const row of approvable) {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(invoiceUrl(row.id, 'approve'), {});
        done += 1;
      }
      setSnackbar({ open: true, message: t('purchaseLedger.list.bulkApproved', { count: done }), severity: 'success' });
    } catch (error) {
      const missing = error.response?.data?.missing;
      const detail = missing?.length
        ? `${purchaseErrorMessage(t, error)} ${missing.map((m) => t(`purchaseLedger.approval.missing.${m}`, { defaultValue: m })).join(', ')}`
        : purchaseErrorMessage(t, error);
      setSnackbar({ open: true, message: t('purchaseLedger.list.bulkApproveStopped', { count: done, detail }), severity: 'error' });
    } finally {
      setBulkBusy(false);
      setSelectedIds(new Set());
      refresh();
    }
  };

  const toBatch = () => {
    navigate(`/ostolaskut/maksuun?ids=${payable.map((r) => r.id).join(',')}`);
  };

  const columns = [
    {
      key: 'vendor_name',
      label: t('purchaseLedger.fields.vendor'),
      maxWidth: 260,
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {r.vendor_name || <em>{t('purchaseLedger.vendor.unknown')}</em>}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block" sx={numFont}>
            {r.invoice_number}{r.invoice_number ? ' · ' : ''}{r.number}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'description',
      label: t('purchaseLedger.fields.description'),
      maxWidth: 280,
      hideBelow: 'lg',
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <KindChip kind={r.kind} />
          <Typography variant="body2" color="text.secondary" noWrap>
            {r.description}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'invoice_date',
      label: t('purchaseLedger.fields.invoiceDate'),
      width: '1%',
      sortable: true,
      sortField: 'invoice_date',
      render: (r) => (
        <Typography variant="body2" sx={{ ...numFont, whiteSpace: 'nowrap' }}>
          {formatDateOnly(r.invoice_date)}
        </Typography>
      ),
    },
    {
      key: 'due_date',
      label: t('purchaseLedger.fields.dueDate'),
      width: '1%',
      sortable: true,
      sortField: 'due_date',
      render: (r) => (
        <Typography variant="body2" sx={{ ...numFont, whiteSpace: 'nowrap', color: r.is_overdue ? 'error.main' : 'inherit', fontWeight: r.is_overdue ? 700 : 400 }}>
          {formatDateOnly(r.due_date)}
        </Typography>
      ),
    },
    {
      key: 'total_gross',
      label: t('purchaseLedger.fields.totalGross'),
      align: 'right',
      width: '1%',
      sortable: true,
      sortField: 'total_gross',
      render: (r) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" sx={{ ...numFont, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {formatPriceWithCurrency(toNumber(r.total_gross))}
          </Typography>
          {r.payment_state === 'partial' && (
            <Typography variant="caption" color="text.secondary" sx={numFont}>
              {t('purchaseLedger.fields.openShort')} {formatPriceWithCurrency(toNumber(r.open_amount))}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'status',
      label: t('purchaseLedger.fields.status'),
      width: '1%',
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
          <StatusChip status={r.status} paymentState={r.payment_state} inBatch={r.in_open_batch} />
          <FlagIcons flags={r.flags} />
        </Box>
      ),
    },
    {
      key: 'links',
      label: t('purchaseLedger.links.title'),
      width: '1%',
      hideBelow: 'lg',
      render: (r) => <LinkChip linkCount={r.link_count} flags={r.flags} />,
    },
  ];

  const selectionActions = [];
  if (canApprove) {
    selectionActions.push({
      label: t('purchaseLedger.list.approveSelected', { count: approvable.length }),
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      onClick: bulkApprove,
      color: 'primary',
      variant: 'contained',
      disabled: bulkBusy || approvable.length === 0,
      tooltip: approvable.length === 0 ? t('purchaseLedger.list.nothingApprovable') : undefined,
    });
  }
  if (canPay) {
    selectionActions.push({
      label: t('purchaseLedger.list.addToBatch', { count: payable.length }),
      icon: <PaymentsIcon fontSize="small" />,
      onClick: toBatch,
      color: 'secondary',
      variant: 'outlined',
      disabled: payable.length === 0,
      tooltip: payable.length === 0 ? t('purchaseLedger.list.nothingPayable') : undefined,
    });
  }

  const tabLabel = (key) => {
    const count = key === 'received' ? summary?.received : key === 'disputed' ? summary?.disputed : null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {t(`purchaseLedger.tabs.${key}`)}
        {count != null && count > 0 && <Chip size="small" label={count} sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }} />}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="purchase-invoices-page">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon color="secondary" />
          <Typography variant="h4" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
            {t('purchaseLedger.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canPay && (
            <Button variant="outlined" startIcon={<PaymentsIcon />} onClick={() => navigate('/ostolaskut/maksuun')} data-testid="pl-open-batches">
              {t('purchaseLedger.batches.title')}
            </Button>
          )}
          {canManage && (
            <>
              <Button variant="outlined" startIcon={<EditNoteIcon />} onClick={() => setManualOpen(true)} data-testid="pl-manual-entry">
                {t('purchaseLedger.manual.button')}
              </Button>
              <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setUploadOpen(true)} data-testid="pl-upload">
                {t('purchaseLedger.upload.button')}
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('purchaseLedger.subtitle')}
      </Typography>

      <SummaryStrip summary={summary} />

      {summary?.inbox_failed > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/ostolaskut/saapuneet')} data-testid="pl-open-inbox">
              {t('purchaseLedger.inbox.open')}
            </Button>
          }
        >
          {t('purchaseLedger.inbox.failedBanner', { count: summary.inbox_failed })}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={changeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ minHeight: 40, maxWidth: '100%', '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
        >
          {TABS.map((key) => {
            const Icon = TAB_ICONS[key];
            return <Tab key={key} value={key} label={tabLabel(key)} icon={Icon ? <Icon fontSize="small" /> : undefined} iconPosition="start" data-testid={`pl-tab-${key}`} />;
          })}
        </Tabs>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          placeholder={t('purchaseLedger.list.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 240 } }}
          inputProps={{ 'data-testid': 'pl-search' }}
        />
        <Box sx={{ width: { xs: '100%', sm: 240 } }}>
          <VendorAutocomplete value={vendor} onChange={setVendor} placeholder={t('purchaseLedger.list.allVendors')} />
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('purchaseLedger.fields.costCategory')}</InputLabel>
          <Select value={category} label={t('purchaseLedger.fields.costCategory')} onChange={(e) => setCategory(e.target.value)}>
            <MenuItem value="">
              <em>{t('purchaseLedger.list.allCategories')}</em>
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" type="date" label={t('purchaseLedger.list.dueFrom')} value={dueFrom} onChange={(e) => setDueFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
        <TextField size="small" type="date" label={t('purchaseLedger.list.dueTo')} value={dueTo} onChange={(e) => setDueTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
      </Box>

      {isMobile ? (
        <MobileCards rows={list.data} loading={list.loading} onOpen={(r) => navigate(`/ostolaskut/${r.id}`)} />
      ) : (
        <DataTable
          columns={columns}
          data={list.data}
          loading={list.loading}
          onRowClick={(r) => navigate(`/ostolaskut/${r.id}`)}
          sortField={list.sortField}
          sortDirection={list.sortDirection}
          onSort={list.handleSort}
          selectable={selectionActions.length > 0}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          rowIdField="id"
          selectionActions={selectionActions}
          emptyIcon={<ReceiptLongIcon sx={{ fontSize: 40, color: 'text.disabled' }} />}
          emptyTitle={t('purchaseLedger.list.empty')}
          emptyDescription={t('purchaseLedger.list.emptyHint')}
          pagination={{
            page: list.page,
            totalPages: list.totalPages,
            totalCount: list.totalCount,
            pageSize: list.pageSize,
            onPageChange: list.setPage,
            onPageSizeChange: list.setPageSize,
          }}
          testIdPrefix="pl-row"
        />
      )}

      <UploadDocumentsDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(items) => {
          setSnackbar({ open: true, message: t('purchaseLedger.upload.queued', { count: items?.length || 0 }), severity: 'success' });
          refresh();
        }}
      />
      <ManualInvoiceDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        categories={categories}
        onCreated={(invoice) => {
          setSnackbar({ open: true, message: t('purchaseLedger.manual.created', { number: invoice.number }), severity: 'success' });
          refresh();
          navigate(`/ostolaskut/${invoice.id}`);
        }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PurchaseInvoicesPage;
